/* Silver Play — client-side product search index (Aug 2026 search fix)
   Root cause fixed: Shopify's native /search results page silently returns
   zero products for queries under 4 characters (and mismatches its own
   result count vs. what it renders). This module loads a lightweight JSON
   index of every product once, then does the actual matching in the
   browser — substring match against title, product type, vendor and tags,
   from the very first character typed. Used by both the header/mobile
   predictive dropdown and the full /search results page, so they always
   agree with each other and never silently hide a matching product.

   Relevance fix (Aug 2026): scoring now distinguishes a query matching a
   whole word (e.g. "om" in "Om Rakhi") from a query merely appearing
   inside a longer, unrelated word (e.g. "om" inside "Blossom"). Whole-word
   matches always outrank substring-only matches. No existing match is
   ever dropped — every original substring check is kept as a lower-ranked
   fallback, so results still never silently disappear.

   Typo tolerance (Aug 2026 fix): a misspelled query (e.g. "eairring",
   "trukoise", "rakhee") now still finds the product it meant. This is a
   pure fallback tier — it only runs for a product that scored zero under
   every exact rule above, and it always scores below every exact match, so
   correctly-spelled queries behave exactly as before.

   Spelling variants (Aug 2026 fix): common alternate transliterations of
   the same Hindi/Sanskrit word (e.g. "laxmi" for "lakshmi") aren't typos —
   they can be 3+ letters apart, well outside the typo-distance fallback
   above. These are matched as genuine exact hits (full score), not a
   low-confidence guess, via a small known-synonym list. */
(function () {
  var INDEX_URL = '/search?view=index';
  var CACHE_KEY = 'sp_search_index_v3';
  var _index = null;
  var _fetchPromise = null;

  function normalize(s) {
    return (s || '').toString().toLowerCase();
  }

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Edit distance between two short strings (product-title words are never
  // long enough for this to be a performance concern). Standard DP table.
  function levenshtein(a, b) {
    var la = a.length, lb = b.length;
    if (la === 0) return lb;
    if (lb === 0) return la;
    var row = new Array(lb + 1);
    for (var j = 0; j <= lb; j++) row[j] = j;
    for (var i = 1; i <= la; i++) {
      var prev = row[0];
      row[0] = i;
      for (j = 1; j <= lb; j++) {
        var tmp = row[j];
        row[j] = a.charAt(i - 1) === b.charAt(j - 1)
          ? prev
          : 1 + Math.min(prev, row[j], row[j - 1]);
        prev = tmp;
      }
    }
    return row[lb];
  }

  // How many misspelled letters we tolerate scales with query length — a
  // typo on a short word ("rig" vs "rug") is too ambiguous to guess, but a
  // one- or two-letter slip on a longer word is almost always a real typo.
  function maxTypoDistance(qLen) {
    if (qLen < 4) return 0;
    if (qLen < 6) return 1;
    if (qLen < 9) return 2;
    return 3;
  }

  // Best (smallest) edit distance from the query to any word in `text`,
  // skipping words whose length alone rules them out (a valid Levenshtein
  // lower bound: distance >= |len(a) - len(b)|), so this stays cheap even
  // across a full title/tag list.
  function bestWordDistance(q, text, maxDist) {
    var words = normalize(text).split(/[^a-z0-9]+/);
    var best = null;
    for (var i = 0; i < words.length; i++) {
      var w = words[i];
      if (!w || Math.abs(w.length - q.length) > maxDist) continue;
      var d = levenshtein(q, w);
      if (d > 0 && d <= maxDist && (best === null || d < best)) best = d;
    }
    return best;
  }

  // Fallback-only fuzzy tier: title typos score highest (0.9 down to 0.5),
  // type/vendor/tag typos lower still (0.6 down to 0.2) — but every fuzzy
  // score is deliberately kept below 1, the lowest *exact* match score, so
  // a correctly-spelled result never loses its place to a guessed one.
  function fuzzyScore(p, q) {
    var maxDist = maxTypoDistance(q.length);
    if (maxDist === 0) return 0;

    var titleDist = bestWordDistance(q, p.title, maxDist);
    if (titleDist !== null) return 0.9 - (titleDist - 1) * 0.4;

    var otherDist = bestWordDistance(q, [p.type, p.vendor, p.searchtext].join(' '), maxDist);
    if (otherDist !== null) return 0.6 - (otherDist - 1) * 0.2;

    return 0;
  }

  // Known alternate spellings for the same word — grounded in this catalog's
  // actual Hindi/Sanskrit product and deity names. Add more groups here as
  // new spelling variants come up; each group is symmetric (any member finds
  // every other member).
  var SYNONYMS = [
    ['laxmi', 'lakshmi'],
    ['ganesh', 'ganesha', 'ganpati'],
    ['krishna', 'kishan', 'krisna'],
    ['hanuman', 'hanumanji', 'bajrangbali'],
    ['trishul', 'trishool', 'trident'],
    ['shiv', 'shiva', 'mahadev'],
    ['ram', 'rama'],
    ['jhumka', 'jhumki', 'jhumkas'],
    ['bali', 'baali', 'balis'],
    ['meenakari', 'minakari'],
    ['om', 'aum'],
    ['kundan', 'kundhan'],
    ['rakhi', 'rakhee']
  ];

  function expandQuery(q) {
    for (var i = 0; i < SYNONYMS.length; i++) {
      if (SYNONYMS[i].indexOf(q) > -1) return SYNONYMS[i];
    }
    return [q];
  }

  function fetchIndex() {
    if (_index) return Promise.resolve(_index);
    if (_fetchPromise) return _fetchPromise;

    try {
      var cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.length) {
          _index = parsed;
          return Promise.resolve(_index);
        }
      }
    } catch (e) { /* sessionStorage unavailable — ignore, fall through to fetch */ }

    _fetchPromise = fetch(INDEX_URL, { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('search index fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (data) {
        _index = Array.isArray(data) ? data : [];
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(_index)); } catch (e) {}
        return _index;
      })
      .catch(function () {
        _index = [];
        return _index;
      });
    return _fetchPromise;
  }

  // Exact tiers only — title/type/vendor/tag matching, no typo guessing.
  // Deliberately separate from the fuzzy tier below: this is what runs once
  // per spelling variant (see bestScore), while fuzzy runs only once per
  // search, against what the visitor actually typed.
  function exactScore(p, q, boundaryRe) {
    var title = normalize(p.title);
    if (title.indexOf(q) === 0) return 5;                              // title starts with query
    if (boundaryRe.test(title)) return 4;                              // query is a whole word in the title
    if (title.indexOf(q) > -1) return 3;                               // title contains query as a substring
    var type = normalize(p.type), vendor = normalize(p.vendor);
    if (boundaryRe.test(type) || boundaryRe.test(vendor)) return 2.5;  // whole-word type/vendor match
    if (type.indexOf(q) > -1 || vendor.indexOf(q) > -1) return 2;      // type/vendor substring match
    var searchtext = normalize(p.searchtext);
    if (boundaryRe.test(searchtext)) return 1.5;                       // whole-word tag/material match
    if (searchtext.indexOf(q) > -1) return 1;                          // tag match (e.g. "sterling", "925")
    return 0;
  }

  // Every condition together: exact match, every known spelling variant of
  // the query, and — only if none of those found anything — a typo-distance
  // guess against what was actually typed. The highest score wins, so
  // nothing here can ever rank a product lower than it otherwise would.
  //
  // Fuzzy runs against the ORIGINAL query only, deliberately outside the
  // per-variant loop: letting it also run against every spelling variant
  // (e.g. matching typo-distance of "rakhee" in addition to "rakhi") was
  // multiplying its false-positive surface for no real benefit — synonyms
  // are already-known exact alternates, they don't need a typo guess too.
  function bestScore(p, originalQuery, queries) {
    var best = 0;
    for (var i = 0; i < queries.length; i++) {
      var qi = queries[i];
      var boundaryRe = new RegExp('\\b' + escapeRegex(qi) + '\\b');
      var s = exactScore(p, qi, boundaryRe);
      if (s > best) best = s;
    }
    if (best === 0) best = fuzzyScore(p, originalQuery);
    return best;
  }

  function searchProducts(query, opts) {
    opts = opts || {};
    var limit = opts.limit || 0;
    var q = normalize(query).trim();
    if (!q || !_index) return [];

    var queries = expandQuery(q); // [q] itself, plus any known spelling variants

    var results = [];
    for (var i = 0; i < _index.length; i++) {
      var s = bestScore(_index[i], q, queries);
      if (s > 0) results.push({ p: _index[i], s: s });
    }
    // Stable sort by relevance score (whole-word matches first, then substring, then tag/type)
    results.sort(function (a, b) { return b.s - a.s; });
    var out = results.map(function (r) { return r.p; });
    if (limit) out = out.slice(0, limit);
    return out;
  }

  window.SPSearchIndex = { fetchIndex: fetchIndex, searchProducts: searchProducts };

  // Warm the cache on every page load without blocking anything critical.
  if (window.addEventListener) {
    window.addEventListener('load', function () { fetchIndex(); });
  }
})();